import ExcelJS from 'exceljs';
import { ProjectItem, ProjectTask } from '../types.ts';

/**
 * Generates and downloads a high-executive, professionally styled Excel workbook
 * for a specific Project, complete with corporate styling, KPI metrics,
 * detailed activity breakdown, and a visual Gantt timeline matrix.
 */
export async function exportProjectToExcel(project: ProjectItem) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'GRUPO ISN - SNF - SIC PRO';
  workbook.created = new Date();

  // 1. DETAILED PROJECT SHEET WITH TIMELINE GANTT MATRIX
  const ws = workbook.addWorksheet('Detalle de Proyecto & Gantt', {
    views: [{ showGridLines: true, state: 'frozen', xSplit: 0, ySplit: 13 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });

  // Determine timeline bounds for the visual Gantt columns
  const allDates: number[] = [];
  const startProj = new Date(project.startDate).getTime();
  const endProj = new Date(project.targetEndDate).getTime();
  if (!isNaN(startProj)) allDates.push(startProj);
  if (!isNaN(endProj)) allDates.push(endProj);

  project.tasks.forEach(t => {
    const s = new Date(t.startDate).getTime();
    const e = new Date(t.endDate).getTime();
    if (!isNaN(s)) allDates.push(s);
    if (!isNaN(e)) allDates.push(e);
  });

  const minTime = allDates.length > 0 ? Math.min(...allDates) : Date.now();
  const maxTime = allDates.length > 0 ? Math.max(...allDates) : Date.now() + 30 * 86400000;

  // Generate weekly intervals for the timeline columns (up to 16-24 weeks)
  const timelineWeeks: { label: string; subLabel: string; start: Date; end: Date }[] = [];
  const curr = new Date(minTime);
  curr.setDate(curr.getDate() - curr.getDay() + 1); // Start on Monday

  while (curr.getTime() <= maxTime + 7 * 86400000 && timelineWeeks.length < 26) {
    const weekStart = new Date(curr);
    const weekEnd = new Date(curr);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const monthName = weekStart.toLocaleDateString('es-MX', { month: 'short' }).toUpperCase();
    const dayStart = weekStart.getDate();
    const dayEnd = weekEnd.getDate();

    timelineWeeks.push({
      label: `SEM ${timelineWeeks.length + 1}`,
      subLabel: `${dayStart}-${dayEnd} ${monthName}`,
      start: weekStart,
      end: weekEnd
    });

    curr.setDate(curr.getDate() + 7);
  }

  // Brand Palette Constants
  const BRAND_DARK_BLUE = '1E293B';
  const BRAND_RED = 'E52E2E';
  const BRAND_LIGHT_BG = 'F8FAFC';
  const BRAND_CARD_BG = '0F172A';
  const BORDER_COLOR = 'CBD5E1';

  // --- ROW 1: TOP CORPORATE HEADER BAR ---
  ws.mergeCells('A1:L1');
  const titleCell = ws.getCell('A1');
  titleCell.value = 'GRUPO ISN  |  SISTEMA INTEGRAL DE CONTROL OPERATIVO - SIC PRO';
  titleCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFF' } };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_DARK_BLUE } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(1).height = 24;

  // --- ROW 2: PROJECT TITLE BANNER ---
  ws.mergeCells('A2:L2');
  const subTitleCell = ws.getCell('A2');
  subTitleCell.value = `EXPEDIENTE TÉCNICO Y PLAN DE TRABAJO: ${project.name.toUpperCase()}`;
  subTitleCell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFF' } };
  subTitleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_RED } };
  subTitleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(2).height = 32;

  // --- ROW 3: SUBHEADER INFO ---
  ws.mergeCells('A3:L3');
  const genDateCell = ws.getCell('A3');
  genDateCell.value = `Fecha de Emisión: ${new Date().toLocaleDateString('es-MX', { dateStyle: 'full' })}  •  Sede: ${project.businessUnit}  •  Código: ${project.code}`;
  genDateCell.font = { name: 'Calibri', size: 9, italic: true, color: { argb: '64748B' } };
  genDateCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_LIGHT_BG } };
  genDateCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(3).height = 18;

  // --- ROW 4: EMPTY SPACER ---
  ws.getRow(4).height = 8;

  // --- ROWS 5 to 7: PROJECT METADATA CARD ---
  const metaStyle = {
    fontLabel: { name: 'Calibri', size: 9, bold: true, color: { argb: '475569' } },
    fontVal: { name: 'Calibri', size: 10, bold: true, color: { argb: '0F172A' } },
    fillLabel: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F1F5F9' } } as ExcelJS.Fill,
    fillVal: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } } as ExcelJS.Fill,
    border: {
      top: { style: 'thin', color: { argb: BORDER_COLOR } },
      left: { style: 'thin', color: { argb: BORDER_COLOR } },
      bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
      right: { style: 'thin', color: { argb: BORDER_COLOR } },
    } as Partial<ExcelJS.Borders>
  };

  // Row 5
  ws.getCell('A5').value = 'CÓDIGO DE PROYECTO:';
  ws.getCell('A5').font = metaStyle.fontLabel;
  ws.getCell('A5').fill = metaStyle.fillLabel;
  ws.getCell('A5').border = metaStyle.border;

  ws.getCell('B5').value = project.code;
  ws.getCell('B5').font = { ...metaStyle.fontVal, color: { argb: '0284C7' } };
  ws.getCell('B5').fill = metaStyle.fillVal;
  ws.getCell('B5').border = metaStyle.border;

  ws.getCell('C5').value = 'CLIENTE / PLANTA:';
  ws.getCell('C5').font = metaStyle.fontLabel;
  ws.getCell('C5').fill = metaStyle.fillLabel;
  ws.getCell('C5').border = metaStyle.border;

  ws.getCell('D5').value = project.clientName;
  ws.getCell('D5').font = metaStyle.fontVal;
  ws.getCell('D5').fill = metaStyle.fillVal;
  ws.getCell('D5').border = metaStyle.border;

  ws.getCell('E5').value = 'LÍDER / PM RESPONSABLE:';
  ws.getCell('E5').font = metaStyle.fontLabel;
  ws.getCell('E5').fill = metaStyle.fillLabel;
  ws.getCell('E5').border = metaStyle.border;

  ws.mergeCells('F5:G5');
  ws.getCell('F5').value = project.manager;
  ws.getCell('F5').font = metaStyle.fontVal;
  ws.getCell('F5').fill = metaStyle.fillVal;
  ws.getCell('F5').border = metaStyle.border;

  ws.getCell('H5').value = 'CATEGORÍA:';
  ws.getCell('H5').font = metaStyle.fontLabel;
  ws.getCell('H5').fill = metaStyle.fillLabel;
  ws.getCell('H5').border = metaStyle.border;

  ws.getCell('I5').value = project.category;
  ws.getCell('I5').font = metaStyle.fontVal;
  ws.getCell('I5').fill = metaStyle.fillVal;
  ws.getCell('I5').border = metaStyle.border;

  ws.getCell('J5').value = 'PRIORIDAD:';
  ws.getCell('J5').font = metaStyle.fontLabel;
  ws.getCell('J5').fill = metaStyle.fillLabel;
  ws.getCell('J5').border = metaStyle.border;

  ws.mergeCells('K5:L5');
  ws.getCell('K5').value = project.priority;
  ws.getCell('K5').font = { ...metaStyle.fontVal, color: { argb: project.priority === 'CRITICA' || project.priority === 'ALTA' ? 'DC2626' : '0284C7' } };
  ws.getCell('K5').fill = metaStyle.fillVal;
  ws.getCell('K5').border = metaStyle.border;

  // Row 6
  ws.getCell('A6').value = 'FECHA INICIO:';
  ws.getCell('A6').font = metaStyle.fontLabel;
  ws.getCell('A6').fill = metaStyle.fillLabel;
  ws.getCell('A6').border = metaStyle.border;

  ws.getCell('B6').value = project.startDate;
  ws.getCell('B6').font = metaStyle.fontVal;
  ws.getCell('B6').fill = metaStyle.fillVal;
  ws.getCell('B6').border = metaStyle.border;

  ws.getCell('C6').value = 'FECHA COMPROMISO (FIN):';
  ws.getCell('C6').font = metaStyle.fontLabel;
  ws.getCell('C6').fill = metaStyle.fillLabel;
  ws.getCell('C6').border = metaStyle.border;

  ws.getCell('D6').value = project.targetEndDate;
  ws.getCell('D6').font = metaStyle.fontVal;
  ws.getCell('D6').fill = metaStyle.fillVal;
  ws.getCell('D6').border = metaStyle.border;

  ws.getCell('E6').value = 'ESTADO GENERAL:';
  ws.getCell('E6').font = metaStyle.fontLabel;
  ws.getCell('E6').fill = metaStyle.fillLabel;
  ws.getCell('E6').border = metaStyle.border;

  const statusColor = project.status === 'COMPLETADO' ? '059669' :
                      project.status === 'EN_TIEMPO' ? '10B981' :
                      project.status === 'EN_RIESGO' ? 'D97706' :
                      project.status === 'RETRASADO' ? 'DC2626' : '64748B';

  ws.mergeCells('F6:G6');
  ws.getCell('F6').value = project.status.replace('_', ' ');
  ws.getCell('F6').font = { name: 'Calibri', size: 10, bold: true, color: { argb: statusColor } };
  ws.getCell('F6').fill = metaStyle.fillVal;
  ws.getCell('F6').border = metaStyle.border;

  ws.getCell('H6').value = 'AVANCE GLOBAL:';
  ws.getCell('H6').font = metaStyle.fontLabel;
  ws.getCell('H6').fill = metaStyle.fillLabel;
  ws.getCell('H6').border = metaStyle.border;

  ws.getCell('I6').value = `${project.progressPercent}%`;
  ws.getCell('I6').font = { name: 'Calibri', size: 12, bold: true, color: { argb: '059669' } };
  ws.getCell('I6').fill = metaStyle.fillVal;
  ws.getCell('I6').border = metaStyle.border;

  ws.getCell('J6').value = 'PRESUPUESTO:';
  ws.getCell('J6').font = metaStyle.fontLabel;
  ws.getCell('J6').fill = metaStyle.fillLabel;
  ws.getCell('J6').border = metaStyle.border;

  ws.mergeCells('K6:L6');
  ws.getCell('K6').value = project.budgetTotal ? `$${project.budgetTotal.toLocaleString('es-MX')} ${project.budgetCurrency || 'MXN'}` : 'N/A';
  ws.getCell('K6').font = metaStyle.fontVal;
  ws.getCell('K6').fill = metaStyle.fillVal;
  ws.getCell('K6').border = metaStyle.border;

  // Row 7: Description
  ws.getCell('A7').value = 'ALCANCE / OBJETIVO:';
  ws.getCell('A7').font = metaStyle.fontLabel;
  ws.getCell('A7').fill = metaStyle.fillLabel;
  ws.getCell('A7').border = metaStyle.border;

  ws.mergeCells('B7:L7');
  ws.getCell('B7').value = `${project.description}  |  ${project.scopeObjectives || ''}`;
  ws.getCell('B7').font = { name: 'Calibri', size: 9, italic: true, color: { argb: '334155' } };
  ws.getCell('B7').fill = metaStyle.fillVal;
  ws.getCell('B7').border = metaStyle.border;
  ws.getCell('B7').alignment = { wrapText: true, vertical: 'middle' };
  ws.getRow(7).height = 24;

  // --- ROW 8: EMPTY SPACER ---
  ws.getRow(8).height = 10;

  // --- ROWS 9 to 10: EXECUTIVE KPI SUMMARY BOXES ---
  const totalTasks = project.tasks.length;
  const completedTasks = project.tasks.filter(t => t.status === 'COMPLETADA').length;
  const inProgressTasks = project.tasks.filter(t => t.status === 'EN_PROCESO').length;
  const delayedTasks = project.tasks.filter(t => t.status === 'RETRASADA' || t.status === 'BLOQUEADA').length;
  const pendingTasks = project.tasks.filter(t => t.status === 'POR_INICIAR' || t.status === 'EN_REVISION').length;

  const kpiBoxes = [
    { rangeTitle: 'A9:B9', rangeVal: 'A10:B10', title: 'TOTAL ACTIVIDADES', val: `${totalTasks} Tareas`, color: '1E293B' },
    { rangeTitle: 'C9:D9', rangeVal: 'C10:D10', title: 'COMPLETADAS', val: `${completedTasks} (${totalTasks > 0 ? Math.round((completedTasks/totalTasks)*100) : 0}%)`, color: '059669' },
    { rangeTitle: 'E9:F9', rangeVal: 'E10:F10', title: 'EN EJECUCIÓN', val: `${inProgressTasks} Tareas`, color: '0284C7' },
    { rangeTitle: 'G9:H9', rangeVal: 'G10:H10', title: 'EN RIESGO / BLOQUEADAS', val: `${delayedTasks} Alertas`, color: 'DC2626' },
    { rangeTitle: 'I9:J9', rangeVal: 'I10:J10', title: 'POR INICIAR', val: `${pendingTasks} Pendientes`, color: '64748B' },
    { rangeTitle: 'K9:L9', rangeVal: 'K10:L10', title: '% AVANCE PONDERADO', val: `${project.progressPercent}%`, color: '059669' }
  ];

  kpiBoxes.forEach(box => {
    ws.mergeCells(box.rangeTitle);
    ws.mergeCells(box.rangeVal);

    const tCell = ws.getCell(box.rangeTitle.split(':')[0]);
    tCell.value = box.title;
    tCell.font = { name: 'Calibri', size: 8, bold: true, color: { argb: '475569' } };
    tCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F1F5F9' } };
    tCell.alignment = { horizontal: 'center', vertical: 'middle' };

    const vCell = ws.getCell(box.rangeVal.split(':')[0]);
    vCell.value = box.val;
    vCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: box.color } };
    vCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
    vCell.alignment = { horizontal: 'center', vertical: 'middle' };
  });
  ws.getRow(9).height = 16;
  ws.getRow(10).height = 20;

  // --- ROW 11: EMPTY SPACER ---
  ws.getRow(11).height = 10;

  // --- ROW 12 & 13: TABLE HEADERS (ACTIVITIES ON LEFT, GANTT MATRIX ON RIGHT) ---
  const leftHeaders = [
    { col: 1, key: 'wbs', label: 'WBS', width: 8 },
    { col: 2, key: 'phase', label: 'FASE DEL PROYECTO', width: 28 },
    { col: 3, key: 'name', label: 'ACTIVIDAD / TAREA DE SEGURIDAD U OPERACIÓN', width: 44 },
    { col: 4, key: 'assignee', label: 'RESPONSABLE ASIGNADO', width: 22 },
    { col: 5, key: 'role', label: 'ROL TÉCNICO', width: 18 },
    { col: 6, key: 'startDate', label: 'F. INICIO', width: 12 },
    { col: 7, key: 'endDate', label: 'F. FIN', width: 12 },
    { col: 8, key: 'duration', label: 'DÍAS', width: 8 },
    { col: 9, key: 'status', label: 'ESTADO', width: 14 },
    { col: 10, key: 'priority', label: 'PRIORIDAD', width: 12 },
    { col: 11, key: 'progress', label: '% AVANCE', width: 11 },
    { col: 12, key: 'deliverable', label: 'ENTREGABLE / EVIDENCIA', width: 28 },
  ];

  // Set widths for left columns
  leftHeaders.forEach(h => {
    ws.getColumn(h.col).width = h.width;
  });

  // Apply row 12 and 13 headers for Left table
  leftHeaders.forEach(h => {
    const topCell = ws.getCell(12, h.col);
    const botCell = ws.getCell(13, h.col);
    ws.mergeCells(12, h.col, 13, h.col);

    topCell.value = h.label;
    topCell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FFFFFF' } };
    topCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_DARK_BLUE } };
    topCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    topCell.border = {
      top: { style: 'medium', color: { argb: BRAND_DARK_BLUE } },
      bottom: { style: 'medium', color: { argb: BRAND_RED } },
      left: { style: 'thin', color: { argb: '334155' } },
      right: { style: 'thin', color: { argb: '334155' } }
    };
  });

  // Apply timeline headers on the right starting from Column 13 (M)
  const startTimelineCol = 13;
  timelineWeeks.forEach((tw, idx) => {
    const colNum = startTimelineCol + idx;
    ws.getColumn(colNum).width = 7.5;

    // Top Header: Week number
    const tCell = ws.getCell(12, colNum);
    tCell.value = tw.label;
    tCell.font = { name: 'Calibri', size: 8, bold: true, color: { argb: 'FFFFFF' } };
    tCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: idx % 2 === 0 ? '0F172A' : '1E293B' } };
    tCell.alignment = { vertical: 'middle', horizontal: 'center' };

    // Sub Header: Date range
    const bCell = ws.getCell(13, colNum);
    bCell.value = tw.subLabel;
    bCell.font = { name: 'Calibri', size: 7, color: { argb: 'CBD5E1' } };
    bCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: idx % 2 === 0 ? '1E293B' : '334155' } };
    bCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  });

  ws.getRow(12).height = 20;
  ws.getRow(13).height = 18;

  // --- ROWS 14+: TASK DATA & GANTT CELLS ---
  let currentRow = 14;
  let lastPhase = '';

  project.tasks.forEach((task, tIndex) => {
    // If phase changes, insert a phase divider row
    if (task.phase && task.phase !== lastPhase) {
      lastPhase = task.phase;
      ws.mergeCells(currentRow, 1, currentRow, startTimelineCol + timelineWeeks.length - 1);
      const phaseCell = ws.getCell(currentRow, 1);
      phaseCell.value = `📂  ${task.phase.toUpperCase()}`;
      phaseCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: '1E293B' } };
      phaseCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E2E8F0' } };
      phaseCell.alignment = { vertical: 'middle', indent: 1 };
      ws.getRow(currentRow).height = 22;
      currentRow++;
    }

    const isEven = tIndex % 2 === 0;
    const rowBg = isEven ? 'FFFFFF' : 'F8FAFC';

    // Left Columns
    ws.getCell(currentRow, 1).value = task.wbsCode || `${tIndex + 1}`;
    ws.getCell(currentRow, 1).alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getCell(currentRow, 1).font = { name: 'Calibri', size: 9, bold: true, color: { argb: '475569' } };

    ws.getCell(currentRow, 2).value = task.phase;
    ws.getCell(currentRow, 2).font = { name: 'Calibri', size: 9, color: { argb: '334155' } };

    ws.getCell(currentRow, 3).value = task.isMilestone ? `🔷 ${task.name}` : task.name;
    ws.getCell(currentRow, 3).font = { name: 'Calibri', size: 9, bold: task.isMilestone, color: { argb: task.isMilestone ? '0284C7' : '0F172A' } };

    ws.getCell(currentRow, 4).value = task.assignee;
    ws.getCell(currentRow, 4).font = { name: 'Calibri', size: 9, color: { argb: '1E293B' } };

    ws.getCell(currentRow, 5).value = task.assigneeRole || '-';
    ws.getCell(currentRow, 5).font = { name: 'Calibri', size: 8, italic: true, color: { argb: '64748B' } };

    ws.getCell(currentRow, 6).value = task.startDate;
    ws.getCell(currentRow, 6).alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getCell(currentRow, 6).font = { name: 'Calibri', size: 9, color: { argb: '334155' } };

    ws.getCell(currentRow, 7).value = task.endDate;
    ws.getCell(currentRow, 7).alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getCell(currentRow, 7).font = { name: 'Calibri', size: 9, color: { argb: '334155' } };

    ws.getCell(currentRow, 8).value = task.durationDays;
    ws.getCell(currentRow, 8).alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getCell(currentRow, 8).font = { name: 'Calibri', size: 9, bold: true };

    // Status format
    const tStatCell = ws.getCell(currentRow, 9);
    tStatCell.value = task.status.replace('_', ' ');
    tStatCell.alignment = { horizontal: 'center', vertical: 'middle' };
    const statBg = task.status === 'COMPLETADA' ? 'D1FAE5' :
                   task.status === 'EN_PROCESO' ? 'E0F2FE' :
                   task.status === 'RETRASADA' || task.status === 'BLOQUEADA' ? 'FEE2E2' : 'F1F5F9';
    const statFg = task.status === 'COMPLETADA' ? '065F46' :
                   task.status === 'EN_PROCESO' ? '0369A1' :
                   task.status === 'RETRASADA' || task.status === 'BLOQUEADA' ? '991B1B' : '475569';
    tStatCell.font = { name: 'Calibri', size: 8, bold: true, color: { argb: statFg } };
    tStatCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: statBg } };

    // Priority
    const tPriCell = ws.getCell(currentRow, 10);
    tPriCell.value = task.priority;
    tPriCell.alignment = { horizontal: 'center', vertical: 'middle' };
    tPriCell.font = { name: 'Calibri', size: 8, bold: true, color: { argb: task.priority === 'CRITICA' || task.priority === 'ALTA' ? 'DC2626' : '0284C7' } };

    // Progress %
    const tProgCell = ws.getCell(currentRow, 11);
    tProgCell.value = `${task.progressPercent}%`;
    tProgCell.alignment = { horizontal: 'center', vertical: 'middle' };
    tProgCell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: task.progressPercent === 100 ? '059669' : '0284C7' } };

    ws.getCell(currentRow, 12).value = task.deliverable || '-';
    ws.getCell(currentRow, 12).font = { name: 'Calibri', size: 8, italic: true, color: { argb: '475569' } };

    // Apply baseline background and borders for left columns
    for (let c = 1; c <= 12; c++) {
      if (c !== 9) { // Status has custom background
        ws.getCell(currentRow, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
      }
      ws.getCell(currentRow, c).border = {
        top: { style: 'thin', color: { argb: 'E2E8F0' } },
        bottom: { style: 'thin', color: { argb: 'E2E8F0' } },
        left: { style: 'thin', color: { argb: 'E2E8F0' } },
        right: { style: 'thin', color: { argb: 'E2E8F0' } },
      };
    }

    // --- TIMELINE GANTT BARS RENDERING ON RIGHT COLUMNS ---
    const tStart = new Date(task.startDate).getTime();
    const tEnd = new Date(task.endDate).getTime();

    timelineWeeks.forEach((tw, idx) => {
      const colNum = startTimelineCol + idx;
      const gCell = ws.getCell(currentRow, colNum);

      const wStart = tw.start.getTime();
      const wEnd = tw.end.getTime();

      // Check if task intersects with this week
      const isIntersecting = tStart <= wEnd && tEnd >= wStart;

      if (isIntersecting) {
        if (task.isMilestone) {
          gCell.value = '◆';
          gCell.alignment = { horizontal: 'center', vertical: 'middle' };
          gCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: '0284C7' } };
          gCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E0F2FE' } };
        } else {
          // Color based on completion state
          if (task.progressPercent === 100) {
            gCell.value = '100%';
            gCell.font = { name: 'Calibri', size: 7, bold: true, color: { argb: 'FFFFFF' } };
            gCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '10B981' } }; // Green
          } else if (task.status === 'RETRASADA' || task.status === 'BLOQUEADA') {
            gCell.value = `${task.progressPercent}%`;
            gCell.font = { name: 'Calibri', size: 7, bold: true, color: { argb: 'FFFFFF' } };
            gCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EF4444' } }; // Red
          } else if (task.status === 'EN_PROCESO') {
            gCell.value = `${task.progressPercent}%`;
            gCell.font = { name: 'Calibri', size: 7, bold: true, color: { argb: 'FFFFFF' } };
            gCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0284C7' } }; // Sky Blue
          } else {
            gCell.value = '■';
            gCell.font = { name: 'Calibri', size: 8, color: { argb: '94A3B8' } };
            gCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'CBD5E1' } }; // Slate Gray
          }
          gCell.alignment = { horizontal: 'center', vertical: 'middle' };
        }
      } else {
        // Empty calendar slot
        gCell.value = '';
        gCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isEven ? 'F8FAFC' : 'F1F5F9' } };
      }

      gCell.border = {
        top: { style: 'thin', color: { argb: 'E2E8F0' } },
        bottom: { style: 'thin', color: { argb: 'E2E8F0' } },
        left: { style: 'thin', color: { argb: 'CBD5E1' } },
        right: { style: 'thin', color: { argb: 'CBD5E1' } },
      };
    });

    ws.getRow(currentRow).height = 20;
    currentRow++;
  });

  // --- FOOTER SIGNATURE BLOCKS ---
  currentRow += 2;
  ws.mergeCells(currentRow, 2, currentRow, 5);
  const sig1 = ws.getCell(currentRow, 2);
  sig1.value = `_____________________________________\n${project.manager}\nLíder / Project Manager Responsable`;
  sig1.font = { name: 'Calibri', size: 9, bold: true, color: { argb: '334155' } };
  sig1.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

  ws.mergeCells(currentRow, 8, currentRow, 11);
  const sig2 = ws.getCell(currentRow, 8);
  sig2.value = `_____________________________________\nDirección de Operaciones / HSEQ\nGrupo ISN - Planta ${project.businessUnit}`;
  sig2.font = { name: 'Calibri', size: 9, bold: true, color: { argb: '334155' } };
  sig2.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  ws.getRow(currentRow).height = 45;

  // Generate binary and trigger browser download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `EXPEDIENTE_PROYECTO_${project.code}_${project.businessUnit.replace(/\s+/g, '_')}.xlsx`;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

/**
 * Generates an Excel summary sheet for ALL projects in the business unit / cost center.
 */
export async function exportAllProjectsSummaryToExcel(projects: ProjectItem[], businessUnitName: string) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'GRUPO ISN - SNF - SIC PRO';
  workbook.created = new Date();

  const ws = workbook.addWorksheet('Resumen de Proyectos', {
    views: [{ showGridLines: true, state: 'frozen', xSplit: 0, ySplit: 5 }]
  });

  const BRAND_DARK_BLUE = '1E293B';
  const BRAND_RED = 'E52E2E';

  // Row 1: Header
  ws.mergeCells('A1:J1');
  const tCell = ws.getCell('A1');
  tCell.value = 'GRUPO ISN  |  PORTAFOLIO DE PROYECTOS Y PLANES DE TRABAJO';
  tCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFF' } };
  tCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_DARK_BLUE } };
  tCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(1).height = 22;

  // Row 2: Subheader
  ws.mergeCells('A2:J2');
  const sCell = ws.getCell('A2');
  sCell.value = `UNIDAD DE NEGOCIO: ${businessUnitName.toUpperCase()}  •  TOTAL: ${projects.length} PROYECTOS REGISTRADOS`;
  sCell.font = { name: 'Calibri', size: 13, bold: true, color: { argb: 'FFFFFF' } };
  sCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_RED } };
  sCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(2).height = 30;

  // Row 3: Date
  ws.mergeCells('A3:J3');
  const dCell = ws.getCell('A3');
  dCell.value = `Reporte generado al ${new Date().toLocaleDateString('es-MX', { dateStyle: 'full' })} a las ${new Date().toLocaleTimeString('es-MX')}`;
  dCell.font = { name: 'Calibri', size: 9, italic: true, color: { argb: '64748B' } };
  dCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(3).height = 18;

  ws.getRow(4).height = 8;

  // Headers
  const headers = [
    { col: 1, label: 'CÓDIGO', width: 16 },
    { col: 2, label: 'NOMBRE DEL PROYECTO', width: 38 },
    { col: 3, label: 'CLIENTE / SEDE', width: 26 },
    { col: 4, label: 'CATEGORÍA', width: 20 },
    { col: 5, label: 'LÍDER PM', width: 24 },
    { col: 6, label: 'F. INICIO', width: 13 },
    { col: 7, label: 'F. COMPROMISO', width: 15 },
    { col: 8, label: 'ESTADO', width: 15 },
    { col: 9, label: 'PRIORIDAD', width: 13 },
    { col: 10, label: '% AVANCE', width: 12 },
  ];

  headers.forEach(h => {
    ws.getColumn(h.col).width = h.width;
    const c = ws.getCell(5, h.col);
    c.value = h.label;
    c.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_DARK_BLUE } };
    c.alignment = { vertical: 'middle', horizontal: 'center' };
  });
  ws.getRow(5).height = 24;

  let row = 6;
  projects.forEach((prj, idx) => {
    const isEven = idx % 2 === 0;
    const bg = isEven ? 'FFFFFF' : 'F8FAFC';

    ws.getCell(row, 1).value = prj.code;
    ws.getCell(row, 1).font = { name: 'Calibri', size: 9, bold: true, color: { argb: '0284C7' } };

    ws.getCell(row, 2).value = prj.name;
    ws.getCell(row, 2).font = { name: 'Calibri', size: 9, bold: true };

    ws.getCell(row, 3).value = prj.clientName;
    ws.getCell(row, 4).value = prj.category;
    ws.getCell(row, 5).value = prj.manager;
    ws.getCell(row, 6).value = prj.startDate;
    ws.getCell(row, 7).value = prj.targetEndDate;

    const stCell = ws.getCell(row, 8);
    stCell.value = prj.status.replace('_', ' ');
    stCell.alignment = { horizontal: 'center' };
    const statFg = prj.status === 'COMPLETADO' ? '065F46' :
                   prj.status === 'EN_TIEMPO' ? '059669' :
                   prj.status === 'EN_RIESGO' ? 'D97706' :
                   prj.status === 'RETRASADO' ? 'DC2626' : '64748B';
    stCell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: statFg } };

    ws.getCell(row, 9).value = prj.priority;
    ws.getCell(row, 9).alignment = { horizontal: 'center' };

    const prCell = ws.getCell(row, 10);
    prCell.value = `${prj.progressPercent}%`;
    prCell.alignment = { horizontal: 'center' };
    prCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: '059669' } };

    for (let c = 1; c <= 10; c++) {
      if (c !== 8) ws.getCell(row, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
      ws.getCell(row, c).border = {
        top: { style: 'thin', color: { argb: 'E2E8F0' } },
        bottom: { style: 'thin', color: { argb: 'E2E8F0' } },
        left: { style: 'thin', color: { argb: 'E2E8F0' } },
        right: { style: 'thin', color: { argb: 'E2E8F0' } },
      };
    }

    ws.getRow(row).height = 20;
    row++;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `PORTAFOLIO_PROYECTOS_${businessUnitName.replace(/\s+/g, '_')}.xlsx`;
  anchor.click();
  window.URL.revokeObjectURL(url);
}
